import { useEffect, useState } from 'react';
import { Button, Description, Drawer, FieldError, type OverlayTriggerState } from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import { DrawerTextField } from '../../../components/ui/drawer-form-field';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';

export interface InviteUserPayload {
  email: string;
  displayName?: string;
  password: string;
}

interface InviteUserDrawerProps {
  state: OverlayTriggerState;
  onSubmit: (payload: InviteUserPayload) => Promise<void>;
}

function generateTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 16; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

export function InviteUserDrawer({ state, onSubmit }: InviteUserDrawerProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.isOpen) return;
    setEmail('');
    setDisplayName('');
    setError(null);
    setSubmitting(false);
  }, [state.isOpen]);

  const isValid = email.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        password: generateTempPassword(),
      });
      state.close();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Não foi possível enviar o convite. Tente novamente.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>Convidar usuário</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={drawerBodyClassName}>
              <Description>
                Cria o usuário no Firebase e envia um email para definir a senha. Depois, associe-o
                a um apartamento na página do condomínio.
              </Description>

              <DrawerTextField
                label="Email"
                type="email"
                value={email}
                placeholder="usuario@exemplo.com"
                onChange={setEmail}
                isDisabled={submitting}
              />

              <DrawerTextField
                label="Nome (opcional)"
                value={displayName}
                placeholder="Nome para exibição"
                onChange={setDisplayName}
                isDisabled={submitting}
              />

              {error ? <FieldError>{error}</FieldError> : null}
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!isValid}
                onPress={handleSubmit}
              >
                Enviar convite
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
